import React, { useEffect, useRef } from "react";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import ReactMarkdown from "react-markdown";
import useNewsStore from "../../store/newsStore";
import marketdata from "../../data/Services/marketdata";
import "./NewsNotifications.css";

/**
 * Safe Markdown Renderer
 * Renders markdown with restricted elements for security
 * Allows: links, bold, italic, lists, code
 */
const MarkdownContent = ({ content }) => {
  return (
    <ReactMarkdown
      allowedElements={[
        "p",
        "a",
        "strong",
        "em",
        "ul",
        "ol",
        "li",
        "code",
        "br",
      ]}
      unwrapDisallowed={true}
      components={{
        a: ({ node, ...props }) => (
          <a
            {...props}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "inherit", textDecoration: "underline" }}
            data-evo-id="newsnotifications-link-content"
          />
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );
};

/**
 * NewsNotifications Component
 * Handles display of all news message types:
 * - critical: Modal dialog (requires acknowledgment)
 * - warning: Toast notification (10s auto-dismiss)
 * - info: Toast notification (5s auto-dismiss)
 * - announcement: Persistent banner (manual dismiss)
 */
const NewsNotifications = () => {
  const toastRef = useRef(null);
  const { messages, dismissMessage, acknowledgeMessage, clearExpired } =
    useNewsStore();

  const [criticalMessage, setCriticalMessage] = React.useState(null);
  const [announcementMessage, setAnnouncementMessage] = React.useState(null);

  // Clear expired messages periodically
  useEffect(() => {
    const interval = setInterval(clearExpired, 60000); // Every minute
    return () => clearInterval(interval);
  }, [clearExpired]);

  // Process new messages
  useEffect(() => {
    messages.forEach((message) => {
      if (message.Type === "critical") {
        // Show critical message modal (only one at a time)
        if (!criticalMessage) {
          setCriticalMessage(message);
        }
      } else if (message.Type === "announcement") {
        // Show announcement banner (only one at a time)
        if (!announcementMessage) {
          setAnnouncementMessage(message);
        }
        // If message incorrectly has RequiresAck, acknowledge it to prevent re-delivery
        if (message.RequiresAck) {
          acknowledgeMessage(message.Id, marketdata);
        }
      } else if (message.Type === "warning") {
        // Show warning toast with markdown support
        toastRef.current?.show({
          severity: "warn",
          summary: message.Title,
          detail: <MarkdownContent content={message.Content} />,
          life: 10000,
          closable: true,
        });
        // If message incorrectly has RequiresAck, acknowledge it to prevent re-delivery
        if (message.RequiresAck) {
          acknowledgeMessage(message.Id, marketdata);
        }
      } else if (message.Type === "info") {
        // Show info toast with markdown support
        toastRef.current?.show({
          severity: "info",
          summary: message.Title,
          detail: <MarkdownContent content={message.Content} />,
          life: 5000,
          closable: true,
        });
        // If message incorrectly has RequiresAck, acknowledge it to prevent re-delivery
        if (message.RequiresAck) {
          acknowledgeMessage(message.Id, marketdata);
        }
      }
    });
  }, [messages, criticalMessage, announcementMessage]);

  const handleAcknowledge = (messageId) => {
    acknowledgeMessage(messageId, marketdata);
    setCriticalMessage(null);
  };

  const handleDismiss = (messageId) => {
    dismissMessage(messageId);
    setAnnouncementMessage(null);
  };

  return (
    <>
      {/* Toast container for info/warning messages */}
      <Toast ref={toastRef} position="top-center" />

      {/* Critical Message Modal */}
      <Dialog
        visible={criticalMessage !== null}
        onHide={() => {}} // Cannot be closed without acknowledgment
        header={
          <div className="news-critical-header">
            <i className="pi pi-exclamation-triangle" />
            {criticalMessage?.Title}
          </div>
        }
        modal
        closable={false}
        className="news-critical-dialog"
        draggable={false}
      >
        <div className="news-critical-content">
          <div>
            <MarkdownContent content={criticalMessage?.Content || ""} />
          </div>
          <Button
            label="Entendi"
            severity="danger"
            onClick={() => handleAcknowledge(criticalMessage?.Id)}
            autoFocus
            data-evo-id="newsnotifications-btn-acknowledge"
          />
        </div>
      </Dialog>

      {/* Announcement Banner */}
      {announcementMessage && (
        <div className="news-announcement-banner">
          <div className="news-announcement-content">
            <i className="pi pi-megaphone" />
            <div className="news-announcement-text">
              <strong>{announcementMessage.Title}</strong>
              <div className="news-announcement-markdown">
                <MarkdownContent content={announcementMessage.Content} />
              </div>
            </div>
          </div>
          <Button
            icon="pi pi-times"
            className="p-button-text p-button-plain news-announcement-close"
            onClick={() => handleDismiss(announcementMessage.Id)}
            aria-label="Fechar"
            data-evo-id="newsnotifications-btn-close-announcement"
          />
        </div>
      )}
    </>
  );
};

export default React.memo(NewsNotifications);
